import {
  CalendarDays,
  CalendarX2,
  CheckCircle2,
  Clock3,
  TriangleAlert,
  UsersRound,
} from 'lucide-react'
import { useMemo, type CSSProperties } from 'react'

import { WorkloadHour, type WorkloadHourPhase } from './WorkloadHour'
import { Card } from '../../../ui/Card/Card'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import {
  appointmentStatusCounts,
  appointmentWorkloadGroups,
  observedScheduleRange,
  providerWorkloads,
  type TodayStatusKey,
  type WorkloadGroup,
  workloadNowState,
} from '../today-clinic-model'
import type { TodayScheduleState } from '../use-today-schedule'
import styles from '../OverviewPage.module.css'

const statusClass: Record<TodayStatusKey, string> = {
  confirmed: styles.statusConfirmed!,
  scheduled: styles.statusScheduled!,
  cancelled: styles.statusCancelled!,
  unknown: styles.statusUnknown!,
}

function timeLabel(timestamp: number, timeZone: string) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp)
}

function clinicClock(timestamp: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)
  const read = (type: 'hour' | 'minute') =>
    Number(parts.find((part) => part.type === type)?.value ?? 0)
  return { hour: read('hour') % 24, minute: read('minute') }
}

// Places "now" on the hour columns themselves, so the marker always sits in
// the column of the current clinic hour.
function workloadTimeline(
  buckets: WorkloadGroup[],
  bounds: NonNullable<TodayScheduleState['bounds']>,
  nowMs: number
) {
  const first = buckets[0]!.hour
  const last = buckets[buckets.length - 1]!.hour
  const clock = clinicClock(nowMs, bounds.timeZone)
  const beforeDay = nowMs < bounds.startAtMs
  const afterDay = nowMs >= bounds.endBeforeMs
  const hour = beforeDay ? -1 : afterDay ? 24 : clock.hour
  const column =
    hour < first ? 0 : hour > last ? buckets.length - 1 : hour - first
  const fraction = hour < first ? 0 : hour > last ? 1 : clock.minute / 60
  const phaseOf = (bucketHour: number): WorkloadHourPhase =>
    bucketHour < hour ? 'elapsed' : bucketHour === hour ? 'current' : 'upcoming'
  return { column, fraction, phaseOf }
}

function TodayKpiStrip({
  schedule,
  attentionCount,
  attentionLoading,
}: {
  schedule: TodayScheduleState
  attentionCount: number | null
  attentionLoading: boolean
}) {
  const counts = appointmentStatusCounts(schedule.items)
  const byStatus = Object.fromEntries(
    counts.map((entry) => [entry.key, entry.count])
  )
  const scheduleReady = schedule.status === 'ready'
  const metrics = [
    {
      label: "Today's appointments",
      value: scheduleReady ? schedule.items.length : null,
      icon: CalendarDays,
      tone: styles.kpiPrimary,
    },
    {
      label: 'Confirmed',
      value: scheduleReady ? byStatus.confirmed : null,
      icon: CheckCircle2,
      tone: styles.kpiSuccess,
    },
    {
      label: 'Scheduled',
      value: scheduleReady ? byStatus.scheduled : null,
      icon: Clock3,
      tone: styles.kpiScheduled,
    },
    {
      label: 'Cancelled',
      value: scheduleReady ? byStatus.cancelled : null,
      icon: CalendarX2,
      tone: styles.kpiDanger,
    },
    {
      label: 'Needs attention',
      value: attentionCount,
      icon: TriangleAlert,
      tone: styles.kpiWarning,
    },
  ]

  return (
    <section className={styles.kpiStrip} aria-label="Today's clinic totals">
      {metrics.map((metric) => {
        const Icon = metric.icon
        const isLoading =
          metric.label === 'Needs attention'
            ? attentionLoading
            : schedule.status === 'loading'
        return (
          <div
            className={`${styles.snapshotKpi} ${metric.tone}`}
            key={metric.label}
          >
            <span className={styles.snapshotKpiLabel}>
              <span className={styles.snapshotKpiIcon} aria-hidden="true">
                <Icon size={15} strokeWidth={2} />
              </span>
              <span>{metric.label}</span>
            </span>
            <strong
              aria-label={`${metric.label}: ${metric.value ?? 'unavailable'}`}
            >
              {isLoading ? '…' : (metric.value?.toLocaleString() ?? '—')}
            </strong>
            <small>Today</small>
          </div>
        )
      })}
    </section>
  )
}

function WorkloadChart({
  schedule,
  nowMs,
}: {
  schedule: TodayScheduleState
  nowMs: number
}) {
  const buckets = useMemo(
    () =>
      schedule.bounds
        ? appointmentWorkloadGroups(schedule.items, schedule.bounds.timeZone)
        : [],
    [schedule.bounds, schedule.items]
  )
  const maximum = Math.max(1, ...buckets.map((bucket) => bucket.count))
  const range = observedScheduleRange(schedule.items)
  const nowState = range ? workloadNowState(range, nowMs) : null
  const nowLabel = schedule.bounds
    ? timeLabel(nowMs, schedule.bounds.timeZone)
    : null
  const indicatorLabel =
    nowState?.phase === 'before'
      ? `Before first start · ${nowLabel}`
      : nowState?.phase === 'after'
        ? `Day complete · ${nowLabel}`
        : `Now · ${nowLabel}`
  const timeline =
    nowState && schedule.bounds && buckets.length > 0
      ? workloadTimeline(buckets, schedule.bounds, nowMs)
      : null
  const peak = buckets.reduce<WorkloadGroup | null>(
    (best, bucket) =>
      bucket.count > 0 && (!best || bucket.count > best.count) ? bucket : best,
    null
  )
  const totalStarts = buckets.reduce((sum, bucket) => sum + bucket.count, 0)

  return (
    <Card className={`${styles.panel} ${styles.workloadPanel}`}>
      <header className={styles.panelHeader}>
        <div>
          <span className={styles.sectionLabel}>Clinic flow</span>
          <h3>Today&apos;s workload</h3>
          <p>Active appointment starts across the observed schedule span</p>
        </div>
        {schedule.status === 'ready' && peak ? (
          <dl className={styles.workloadSummary}>
            <div>
              <dt>Starts</dt>
              <dd>{totalStarts.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Peak hour</dt>
              <dd>
                {peak.label}
                <span> · {peak.count.toLocaleString()}</span>
              </dd>
            </div>
          </dl>
        ) : null}
      </header>
      {schedule.status === 'loading' ? (
        <div
          className={styles.chartLoading}
          aria-label="Loading workload chart"
        />
      ) : schedule.status !== 'ready' ? (
        <div className={styles.compactPanelState}>
          <StatePanel
            kind="error"
            title="Workload unavailable"
            description="Today's complete appointment snapshot could not be loaded safely."
          />
        </div>
      ) : buckets.length === 0 ? (
        <div className={styles.quietState}>
          <CalendarDays size={22} aria-hidden="true" />
          <strong>No workload to chart</strong>
          <p>Appointment starts will appear here throughout the clinic day.</p>
        </div>
      ) : (
        <div className={styles.workloadViewport}>
          <div
            className={styles.workloadChart}
            role="group"
            aria-label={`${buckets
              .map(
                (bucket) =>
                  `${bucket.label}: ${bucket.count} appointment${bucket.count === 1 ? '' : 's'} starting`
              )
              .join(', ')}. ${indicatorLabel}`}
            style={
              {
                gridTemplateColumns: `repeat(${buckets.length}, minmax(2.75rem, 1fr))`,
                '--workload-columns': buckets.length,
              } as CSSProperties
            }
          >
            {timeline ? (
              <div
                className={styles.workloadTimeOverlay}
                aria-hidden="true"
                style={
                  {
                    '--now-column': timeline.column,
                    '--now-fraction': timeline.fraction,
                  } as CSSProperties
                }
              >
                <span className={styles.elapsedWash} aria-hidden="true" />
                <span className={styles.nowIndicator} aria-hidden="true" />
                <span className={styles.nowCallout}>
                  <span className={styles.nowCalloutDot} />
                  {indicatorLabel}
                </span>
              </div>
            ) : null}
            {buckets.map((bucket) => (
              <WorkloadHour
                key={bucket.hour}
                bucket={bucket}
                maximum={maximum}
                timeZone={schedule.bounds!.timeZone}
                phase={timeline ? timeline.phaseOf(bucket.hour) : 'upcoming'}
              />
            ))}
          </div>
        </div>
      )}
      {schedule.status === 'ready' && schedule.bounds ? (
        <footer className={styles.panelFooter}>
          <p className={styles.scopeNote}>
            Counts are non-cancelled scheduled starts, not availability ·{' '}
            {schedule.bounds.timeZone}
          </p>
          {timeline ? (
            <ul className={styles.workloadLegend} aria-hidden="true">
              <li>
                <i className={styles.legendElapsed} />
                Elapsed
              </li>
              <li>
                <i className={styles.legendCurrent} />
                This hour
              </li>
              <li>
                <i className={styles.legendUpcoming} />
                Upcoming
              </li>
            </ul>
          ) : null}
        </footer>
      ) : null}
    </Card>
  )
}

function StatusDonut({ schedule }: { schedule: TodayScheduleState }) {
  const counts = appointmentStatusCounts(schedule.items)
  const total = schedule.items.length
  const visibleCounts = counts.filter((entry) => entry.count > 0)
  // A hairline gap separates adjacent segments without changing their share.
  const segmentGap = visibleCounts.length > 1 ? 0.8 : 0
  let offset = 0

  return (
    <Card className={`${styles.panel} ${styles.statusPanel}`}>
      <header className={styles.panelHeader}>
        <div>
          <span className={styles.sectionLabel}>Appointment state</span>
          <h3>Status mix</h3>
          <p>Current status in today&apos;s complete snapshot</p>
        </div>
      </header>
      {schedule.status === 'loading' ? (
        <div className={styles.donutLoading} aria-label="Loading status mix" />
      ) : schedule.status !== 'ready' ? (
        <div className={styles.compactPanelState}>
          <StatePanel
            kind="error"
            title="Status mix unavailable"
            description="Appointment statuses could not be loaded safely."
          />
        </div>
      ) : total === 0 ? (
        <div className={styles.quietState}>
          <CheckCircle2 size={22} aria-hidden="true" />
          <strong>No statuses today</strong>
          <p>Status distribution will appear with today&apos;s appointments.</p>
        </div>
      ) : visibleCounts.length === 1 ? (
        <div className={styles.singleStatus}>
          <span
            className={`${styles.singleStatusDot} ${statusClass[visibleCounts[0]!.key]}`}
            aria-hidden="true"
          />
          <strong>100% {visibleCounts[0]!.label}</strong>
          <p>Today&apos;s appointments currently share one status.</p>
        </div>
      ) : (
        <div className={styles.statusBody}>
          <div className={styles.donutWrap}>
            <svg
              className={styles.donut}
              viewBox="0 0 100 100"
              role="img"
              aria-label={visibleCounts
                .map((entry) => `${entry.label}: ${entry.count}`)
                .join(', ')}
            >
              <circle className={styles.donutTrack} cx="50" cy="50" r="40" />
              {counts.map((entry) => {
                const percentage = (entry.count / total) * 100
                const dashOffset = -offset
                offset += percentage
                if (entry.count === 0) return null
                const visible = Math.max(percentage - segmentGap, 0.4)
                return (
                  <circle
                    className={`${styles.donutSegment} ${statusClass[entry.key]}`}
                    cx="50"
                    cy="50"
                    r="40"
                    pathLength="100"
                    strokeDasharray={`${visible} ${100 - visible}`}
                    strokeDashoffset={dashOffset}
                    key={entry.key}
                  />
                )
              })}
            </svg>
            <span className={styles.donutTotal} aria-hidden="true">
              <strong>{total}</strong>
              <small>Total</small>
            </span>
          </div>
          <ul className={styles.statusLegend}>
            {visibleCounts.map((entry) => (
              <li key={entry.key}>
                <i className={statusClass[entry.key]} aria-hidden="true" />
                <span>{entry.label}</span>
                <strong>{entry.count}</strong>
                <small>{Math.round((entry.count / total) * 100)}%</small>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

function ProviderWorkload({
  schedule,
  nowMs,
}: {
  schedule: TodayScheduleState
  nowMs: number
}) {
  const providers = useMemo(
    () => providerWorkloads(schedule.items, nowMs),
    [nowMs, schedule.items]
  )

  return (
    <Card className={`${styles.panel} ${styles.providerPanel}`}>
      <header className={styles.panelHeader}>
        <div>
          <span className={styles.sectionLabel}>Team view</span>
          <h3>Providers today</h3>
          <p>Appointment share and next scheduled start</p>
        </div>
        {schedule.status === 'ready' ? (
          <span className={styles.providerCount}>
            <UsersRound size={14} aria-hidden="true" />
            {providers.length} workload group{providers.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </header>
      {schedule.status === 'loading' ? (
        <div
          className={styles.rowsLoading}
          aria-label="Loading provider workload"
        >
          {[0, 1, 2].map((row) => (
            <span key={row} />
          ))}
        </div>
      ) : schedule.status !== 'ready' ? (
        <div className={styles.compactPanelState}>
          <StatePanel
            kind="error"
            title="Provider workload unavailable"
            description="Provider assignments could not be loaded safely."
          />
        </div>
      ) : providers.length === 0 ? (
        <div className={styles.quietState}>
          <UsersRound size={22} aria-hidden="true" />
          <strong>No providers today</strong>
          <p>Assigned appointments will appear here.</p>
        </div>
      ) : (
        <ul className={styles.providerList}>
          {providers.map((provider) => (
            <li key={provider.name}>
              <div className={styles.providerCardHeader}>
                <span className={styles.providerAvatar} aria-hidden="true">
                  {provider.name
                    .trim()
                    .replace(/^dr\.?\s+/i, '')
                    .split(/\s+/)
                    .filter(Boolean)
                    .filter(
                      (_, index, parts) =>
                        index === 0 || index === parts.length - 1
                    )
                    .map((part) => Array.from(part)[0])
                    .join('')
                    .toLocaleUpperCase()}
                </span>
                <strong>{provider.name}</strong>
                <span className={styles.providerAppointmentCount}>
                  {provider.appointmentCount}
                </span>
              </div>
              <span className={styles.providerLoad} aria-hidden="true">
                <span style={{ width: `${provider.shareOfToday * 100}%` }} />
              </span>
              <p>
                {provider.appointmentCount.toLocaleString()} appointment
                {provider.appointmentCount === 1 ? '' : 's'} ·{' '}
                {Math.round(provider.shareOfToday * 100)}% of today&apos;s
                appointment load
              </p>
              <small>
                <Clock3 size={13} aria-hidden="true" />
                {provider.nextStartAtMs !== null && schedule.bounds
                  ? `Next: ${timeLabel(provider.nextStartAtMs, schedule.bounds.timeZone)}`
                  : 'No later starts today'}
              </small>
            </li>
          ))}
        </ul>
      )}
      {schedule.status === 'ready' ? (
        <footer className={styles.panelFooter}>
          <p className={styles.scopeNote}>
            Share is based on non-cancelled appointment count only; appointment
            duration is not inferred.
          </p>
        </footer>
      ) : null}
    </Card>
  )
}

export function TodayClinicSnapshot({
  schedule,
  attentionCount,
  attentionLoading,
  nowMs,
}: {
  schedule: TodayScheduleState
  attentionCount: number | null
  attentionLoading: boolean
  nowMs: number
}) {
  return (
    <>
      <TodayKpiStrip
        schedule={schedule}
        attentionCount={attentionCount}
        attentionLoading={attentionLoading}
      />
      <div className={styles.insightGrid}>
        <WorkloadChart schedule={schedule} nowMs={nowMs} />
        <StatusDonut schedule={schedule} />
      </div>
      <ProviderWorkload schedule={schedule} nowMs={nowMs} />
    </>
  )
}
